import { useState, useMemo, useEffect } from "react";
import useAppStore from "src/stores/appStore";
import { defaultCollectionsLanguagesSelector } from "src/stores/selectors/collectionsSelectors";
import { matchesSearch, matchesFilter } from "../HyroTableContainer.utils";

const EMPTY_ARRAY = [];

export const useClientPagination = ({
  initialFilters = EMPTY_ARRAY,
  data = EMPTY_ARRAY,
  onFiltersChange,
  searchFields
}) => {
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(initialFilters);

  // Sync dropdown options when initialFilters changes after async data load (dynamic options are empty
  // until data arrives). Done during render, not in an effect, so the update lands before paint and
  // never flickers; the synced-key guard runs it once per real change despite a new prop ref each
  // render. Active selections are preserved by key.
  const optionsKey = JSON.stringify(initialFilters.map(f => ({ key: f.key, options: f.options })));
  const [syncedOptionsKey, setSyncedOptionsKey] = useState(optionsKey);
  if (optionsKey !== syncedOptionsKey) {
    setSyncedOptionsKey(optionsKey);
    setFilters(prev => initialFilters.map(next => {
      const existing = prev.find(f => f.key === next.key);
      return existing
        ? { ...next, isActive: existing.isActive, value: existing.value, activeSince: existing.activeSince, operator: existing.operator }
        : next;
    }));
  }

  const defaultLanguage = useAppStore(defaultCollectionsLanguagesSelector);

  const rows = useMemo(() => {
    const context = { defaultLanguage };
    const initialRows = data || [];
    let result = initialRows;

    if (searchText) {
      result = result.filter(row => matchesSearch(row, searchText, searchFields));
    }

    if (filters?.length) {
      result = result.filter(row => filters.every(filterItem => matchesFilter(row, filterItem, context)));
    }

    return result;
  }, [data, filters, searchText, searchFields, defaultLanguage]);

  useEffect(() => {
    if (onFiltersChange && JSON.stringify(filters) !== JSON.stringify(initialFilters)) {
      onFiltersChange(filters);
    }
  }, [filters, initialFilters, onFiltersChange]);

  return {
    searchText,
    setSearchText,
    filters,
    setFilters,
    rows
  };
};
